import { BlogPostPage } from "./BlogPostPage";
import { doorwayTeamsPost as post } from "./DoorwayTeamsPostData";

export function DoorwayTeamsPost() {
  return (
    <BlogPostPage post={post}>
      <p className="blog-lede">A pattern keeps showing up in my conversations with other marketing engineers.</p>

      <p>Someone builds a small internal app, usually in an afternoon or over a weekend. Maybe it clusters keywords, builds UTMs, summarizes customer calls, or pulls data from three places and formats it for the weekly deck.</p>

      <p>The app works. The person who made it is thrilled. Then a coworker asks for access.</p>

      <p>That is when things get complicated.</p>

      <h2>The first user is easy</h2>

      <p>Building an app is easier than it has ever been. AI coding tools can take you from an idea to a working version in hours. If you can describe the workflow clearly and have enough patience to debug the strange parts, you can make something genuinely useful.</p>

      <p>The first user is also easy because the first user is you.</p>

      <p>You know where the app lives. You know which credentials it needs. You remember the tiny rules that never made it into the interface. You know which button takes a few seconds and should only be clicked once.</p>

      <p>The app fits neatly inside your own working habits.</p>

      <p>The second user changes the shape of the problem.</p>

      <h2>What arrives with the second user</h2>

      <p>When someone else needs your app, all the context you were carrying in your head has to become part of the experience.</p>

      <p>First, the app needs a stable home. A local server running on your laptop will not help someone across the office or in another time zone.</p>

      <p>Then you need identity. Your coworker should sign in as themselves and use their own session. Shared credentials become awkward quickly and make access harder to manage.</p>

      <p>Identity also creates a second question: what should this person be allowed to open? Google sign-in can confirm who someone is. The app still needs a clear access policy that decides which people belong in which workspace and which apps they can use.</p>

      <p>Ownership matters too. If the original creator leaves the company, the app should stay with the team. Someone needs the ability to add a new creator, remove an old account, and keep the app available without reconstructing the original setup.</p>

      <p>Then there is discovery. A link buried in a six-month-old Slack thread is a fragile internal app directory. New teammates need a simple way to see what exists, what each app does, and who owns it.</p>

      <p>Offboarding completes the picture. When someone leaves, their access should disappear promptly, including any active sessions.</p>

      <p>Every item on that list is solvable. Together, they become a second product wrapped around the app you wanted to share.</p>

      <h2>The bottleneck is moving</h2>

      <p>Marketing teams are going to build more software.</p>

      <p>The cost of creating a useful first version keeps falling. A marketing engineer who once built one internal app each quarter might now build several. A marketer without a traditional engineering background can make a working app with a good prompt, a clear process, and some persistence.</p>

      <p>Teams can create more apps than they can comfortably host, secure, organize, and maintain. The app itself may take an afternoon. Turning it into something ten coworkers can rely on can take much longer.</p>

      <p>Most teams bridge the gap with a mixture of shared passwords, scattered links, one-off hosting accounts, and documentation that slowly drifts out of date. It works for a while. Each new app and each new user adds another small piece of access and ownership work.</p>

      <p>This is the move from single-player to multiplayer mode.</p>

      <h2>What Doorway Teams gives your company</h2>

      <p>Doorway Teams gives marketing teams a private sharing layer for the apps they make.</p>

      <p>You publish an app through Doorway and choose who should have access. A workspace admin can approve specific email addresses or create an explicit policy for a verified Google Workspace domain.</p>

      <p>Coworkers sign in with Google. Doorway confirms their identity, applies the workspace access policy, and shows them the internal apps available to them.</p>

      <p>The team gets one private place to find its apps, with clear ownership and descriptions. Over time, shared workspace ownership, roles, audit history, and immediate session revocation give admins the controls they need to apply their internal security policies.</p>

      <ol className="blog-steps">
        <li><span>1</span><p><strong>Publish the app.</strong> Choose the finished app and give it a stable home.</p></li>
        <li><span>2</span><p><strong>Set the guest list.</strong> Approve a Google Workspace domain or specific people.</p></li>
        <li><span>3</span><p><strong>Invite the team.</strong> Coworkers sign in and see the apps available to them.</p></li>
        <li><span>4</span><p><strong>Keep access current.</strong> Add or remove people as the team changes.</p></li>
      </ol>

      <p>The app creator can stay focused on the useful thing they made. Doorway handles the front door and the guest list.</p>

      <h2>Marketing teams need a sharing layer</h2>

      <p>The next generation of internal marketing software will come from the people closest to the work.</p>

      <p>They understand the awkward handoff in the campaign process. They know which report still takes two hours every Monday. They see the spreadsheet that has quietly become part of the company infrastructure.</p>

      <p>More of those people can now turn their ideas into working apps. That is exciting. It also means companies need a reliable path from a useful local creation to a private team resource.</p>

      <p>Doorway Teams is built for that transition.</p>

      <p>If your marketing team is already making internal apps and running into the second-user problem, I would love to learn what you have built, how you share it today, and where multiplayer mode starts to hurt.</p>
    </BlogPostPage>
  );
}
